import {
  Autocomplete as MuiAutocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";

import { getGoogleMapsApiKey } from "config/runtimeConfig.js";
import { CssTextField } from "qubCommon/Inputs/CssTextField";
import { parseGoogleAddress } from "util/parseGoogleAddress";
import { buildAddressPreviewEntries } from "./previewEntries";
import {
  getEnterAction,
  shouldFetchSuggestionsOnSubmit,
  shouldHideSuggestionsOnInput,
} from "./searchMode";

export const GOOGLE_MAPS_LIBRARIES = ["places"];

const MAP_CONTAINER_STYLE = { width: "100%", height: "300px" };
const GREECE_CENTER = { lat: 39.0742, lng: 21.8243 };
const GREECE_ZOOM = 6;
const PIN_ZOOM = 16;

const parseCoordinate = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(String(value).trim().replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

const getPosition = (place) => {
  const location = place?.location || place?.geometry?.location;
  const lat =
    typeof location?.lat === "function" ? location.lat() : location?.lat;
  const lng =
    typeof location?.lng === "function" ? location.lng() : location?.lng;

  return parseCoordinate(lat) === null || parseCoordinate(lng) === null
    ? null
    : { lat: parseCoordinate(lat), lng: parseCoordinate(lng) };
};

const getSuggestionLabel = (prediction) => {
  if (typeof prediction?.text?.toString === "function")
    return prediction.text.toString();
  return prediction?.description || "";
};

const GmapsPopulator = ({
  disabled = false,
  fieldNames,
  apiKey,
  onResolved,
}) => {
  const { setValue, watch, getValues } = useFormContext();
  const { fieldsVariant } = useSelector((state) => state.customization);
  const placesLibraryRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const requestIdRef = useRef(0);
  const debounceTimerRef = useRef(null);

  const fields = useMemo(
    () => ({
      street: "street",
      streetNumber: "streetNumber",
      city: "city",
      zip: "zip",
      latitude: "latitude",
      longitude: "longitude",
      ...fieldNames,
    }),
    [fieldNames]
  );

  const currentStreet = watch(fields.street);
  const currentStreetNumber = watch(fields.streetNumber);
  const currentCity = watch(fields.city);
  const currentZip = watch(fields.zip);
  const currentLat = watch(fields.latitude);
  const currentLong = watch(fields.longitude);

  const resolvedApiKey = getGoogleMapsApiKey(apiKey);
  const { isLoaded, loadError } = useJsApiLoader({
    id: "mhd-google-maps-script",
    googleMapsApiKey: resolvedApiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
    language: "el",
    region: "GR",
  });

  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = useState([]);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [liveSuggestions, setLiveSuggestions] = useState(false);
  const [placesReady, setPlacesReady] = useState(false);
  const [placesError, setPlacesError] = useState("");
  const [sessionToken, setSessionToken] = useState(null);
  const [previewPosition, setPreviewPosition] = useState(null);
  const [pendingValues, setPendingValues] = useState(null);
  const [resolvingPosition, setResolvingPosition] = useState(false);

  const composeCurrentAddress = useCallback(
    () =>
      [
        [currentStreet, currentStreetNumber].filter(Boolean).join(" ").trim(),
        currentZip,
        currentCity,
        "Ελλάδα",
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
        .join(", "),
    [currentCity, currentStreet, currentStreetNumber, currentZip]
  );

  const buildResolvedValues = useCallback(
    (place, fallbackAddress = "") => {
      const position = getPosition(place);
      if (!position) return null;

      const formattedAddress =
        place?.formattedAddress ||
        place?.formatted_address ||
        fallbackAddress ||
        "";
      const addressComponents =
        place?.addressComponents || place?.address_components || [];
      const parsedAddress = parseGoogleAddress(
        addressComponents,
        formattedAddress
      );
      const values = {
        [fields.latitude]: position.lat,
        [fields.longitude]: position.lng,
      };

      if (parsedAddress.street) values[fields.street] = parsedAddress.street;
      if (parsedAddress.streetNumber)
        values[fields.streetNumber] = parsedAddress.streetNumber;
      if (parsedAddress.city) values[fields.city] = parsedAddress.city;
      if (parsedAddress.zip) values[fields.zip] = parsedAddress.zip;

      return { formattedAddress, position, values };
    },
    [fields]
  );

  const geocodeText = useCallback(
    async (addressText) => {
      const address = String(addressText || "").trim();
      if (!address || !window.google?.maps?.Geocoder) return null;

      try {
        const geocoder = new window.google.maps.Geocoder();
        const response = await geocoder.geocode({
          address,
          componentRestrictions: { country: "GR" },
          language: "el",
          region: "gr",
        });
        return buildResolvedValues(response?.results?.[0], address);
      } catch {
        return null;
      }
    },
    [buildResolvedValues]
  );

  const reverseGeocode = useCallback(
    async (position) => {
      if (!window.google?.maps?.Geocoder) return null;

      try {
        const geocoder = new window.google.maps.Geocoder();
        const response = await geocoder.geocode({
          location: position,
          language: "el",
          region: "gr",
        });
        return buildResolvedValues(response?.results?.[0]);
      } catch {
        return null;
      }
    },
    [buildResolvedValues]
  );

  useEffect(() => {
    let cancelled = false;

    const initializePlaces = async () => {
      if (!isLoaded || !window.google?.maps) return;

      try {
        const placesLibrary =
          typeof window.google.maps.importLibrary === "function"
            ? await window.google.maps.importLibrary("places")
            : window.google.maps.places;
        if (cancelled) return;
        placesLibraryRef.current = placesLibrary;
        setPlacesReady(
          Boolean(
            placesLibrary?.AutocompleteSuggestion ||
              window.google.maps.places?.AutocompleteService
          )
        );
      } catch {
        if (!cancelled)
          setPlacesError("Η αναζήτηση Google Places δεν είναι διαθέσιμη.");
      }
    };

    initializePlaces();
    return () => {
      cancelled = true;
    };
  }, [isLoaded]);

  const fetchLegacySuggestions = useCallback(async (input) => {
    if (!window.google?.maps?.places?.AutocompleteService) return [];
    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService();
    }

    return new Promise((resolve) => {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input,
          componentRestrictions: { country: "gr" },
          language: "el",
        },
        (predictions, status) => {
          const validStatus =
            status === window.google.maps.places.PlacesServiceStatus.OK ||
            status ===
              window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS;
          resolve(
            validStatus
              ? (predictions || []).map((prediction) => ({
                  label: prediction.description,
                  placeId: prediction.place_id,
                  mode: "legacy",
                }))
              : []
          );
        }
      );
    });
  }, []);

  const fetchSuggestions = useCallback(
    async (inputText) => {
      const input = String(inputText || "").trim();
      if (!placesReady || input.length < 3) {
        setOptions([]);
        setOptionsOpen(false);
        return;
      }

      const requestId = ++requestIdRef.current;
      setLoadingSuggestions(true);
      setPlacesError("");

      try {
        const placesLibrary = placesLibraryRef.current;
        let nextOptions = [];

        if (placesLibrary?.AutocompleteSuggestion) {
          const token =
            sessionToken ||
            (placesLibrary.AutocompleteSessionToken
              ? new placesLibrary.AutocompleteSessionToken()
              : null);
          if (!sessionToken && token) setSessionToken(token);

          const response =
            await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions(
              {
                input,
                includedRegionCodes: ["gr"],
                language: "el",
                region: "gr",
                ...(token ? { sessionToken: token } : {}),
              }
            );
          nextOptions = (response?.suggestions || [])
            .map((item) => ({
              label: getSuggestionLabel(item?.placePrediction),
              prediction: item?.placePrediction,
              mode: "new",
            }))
            .filter((item) => item.label && item.prediction);
        } else {
          nextOptions = await fetchLegacySuggestions(input);
        }

        if (requestId !== requestIdRef.current) return;
        setOptions(nextOptions);
        setOptionsOpen(nextOptions.length > 0);
      } catch {
        const legacyOptions = await fetchLegacySuggestions(input);
        if (requestId !== requestIdRef.current) return;
        setOptions(legacyOptions);
        setOptionsOpen(legacyOptions.length > 0);
        if (!legacyOptions.length)
          setPlacesError("Δεν βρέθηκαν προτάσεις διεύθυνσης.");
      } finally {
        if (requestId === requestIdRef.current)
          setLoadingSuggestions(false);
      }
    },
    [fetchLegacySuggestions, placesReady, sessionToken]
  );

  useEffect(() => {
    if (!open || !liveSuggestions) return undefined;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(
      () => fetchSuggestions(searchValue),
      250
    );

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [fetchSuggestions, liveSuggestions, open, searchValue]);

  const setResolvedPreview = useCallback((resolved) => {
    if (!resolved) {
      setPlacesError("Δεν βρέθηκε η διεύθυνση στο Google Maps.");
      return;
    }
    setPreviewPosition(resolved.position);
    setPendingValues(resolved.values);
    setSearchValue(resolved.formattedAddress || searchValue);
    setOptions([]);
    setOptionsOpen(false);
    setPlacesError("");
  }, [searchValue]);

  const resolveLegacyPlace = useCallback(
    async (placeId) => {
      if (!placeId || !window.google?.maps?.places?.PlacesService) return null;
      if (!placesServiceRef.current) {
        placesServiceRef.current = new window.google.maps.places.PlacesService(
          document.createElement("div")
        );
      }

      return new Promise((resolve) => {
        placesServiceRef.current.getDetails(
          {
            placeId,
            fields: ["formatted_address", "address_components", "geometry"],
            language: "el",
            region: "gr",
          },
          (place, status) => {
            resolve(
              status ===
                window.google.maps.places.PlacesServiceStatus.OK && place
                ? buildResolvedValues(place)
                : null
            );
          }
        );
      });
    },
    [buildResolvedValues]
  );

  const handleSuggestionSelect = async (option) => {
    if (!option) return;

    if (
      typeof option === "string" &&
      shouldFetchSuggestionsOnSubmit(liveSuggestions)
    ) {
      await fetchSuggestions(option);
      return;
    }

    setResolvingPosition(true);

    try {
      if (typeof option === "string") {
        const resolved = await geocodeText(option);
        setResolvedPreview(resolved);
        return;
      }

      let resolved;
      if (option.mode === "new") {
        const place = option.prediction.toPlace();
        await place.fetchFields({
          fields: ["formattedAddress", "addressComponents", "location"],
        });
        resolved = buildResolvedValues(place, option.label);
        setSessionToken(null);
      } else {
        resolved = await resolveLegacyPlace(option.placeId);
      }
      setResolvedPreview(resolved);
    } catch {
      setPlacesError("Δεν ήταν δυνατός ο εντοπισμός της διεύθυνσης.");
    } finally {
      setResolvingPosition(false);
    }
  };

  const handleTypedSearch = async () => {
    if (shouldFetchSuggestionsOnSubmit(liveSuggestions)) {
      await fetchSuggestions(searchValue);
      return;
    }

    setResolvingPosition(true);
    const resolved = await geocodeText(searchValue);
    setResolvedPreview(resolved);
    setResolvingPosition(false);
  };

  const handleMapPosition = useCallback(
    async (position) => {
      if (disabled || !position) return;
      setPreviewPosition(position);
      setPendingValues({
        [fields.latitude]: position.lat,
        [fields.longitude]: position.lng,
      });
      setSearchValue("Επιλεγμένο σημείο στον χάρτη");
      setResolvingPosition(true);
      setPlacesError("");

      const resolved = await reverseGeocode(position);
      if (resolved) {
        setPreviewPosition(position);
        setPendingValues({
          ...resolved.values,
          [fields.latitude]: position.lat,
          [fields.longitude]: position.lng,
        });
        setSearchValue(resolved.formattedAddress);
      }
      setResolvingPosition(false);
    },
    [disabled, fields, reverseGeocode]
  );

  const handleMapClick = useCallback(
    (event) => {
      if (event?.placeId && typeof event.stop === "function") event.stop();
      const lat = event?.latLng?.lat?.();
      const lng = event?.latLng?.lng?.();
      if (parseCoordinate(lat) === null || parseCoordinate(lng) === null)
        return;
      handleMapPosition({ lat, lng });
    },
    [handleMapPosition]
  );

  const handleMarkerDragEnd = useCallback(
    (event) => {
      const lat = event?.latLng?.lat?.();
      const lng = event?.latLng?.lng?.();
      if (parseCoordinate(lat) === null || parseCoordinate(lng) === null)
        return;
      handleMapPosition({ lat, lng });
    },
    [handleMapPosition]
  );

  const handleOpen = async () => {
    const lat = parseCoordinate(currentLat);
    const lng = parseCoordinate(currentLong);
    const existingPosition =
      lat !== null && lng !== null ? { lat, lng } : null;
    const currentAddress = composeCurrentAddress();

    setSearchValue(currentAddress);
    setPreviewPosition(existingPosition);
    setPendingValues(null);
    setOptions([]);
    setOptionsOpen(false);
    setPlacesError("");
    setOpen(true);

    if (!existingPosition && isLoaded && currentAddress) {
      const resolved = await geocodeText(currentAddress);
      if (resolved) setPreviewPosition(resolved.position);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setConfirmOpen(false);
    setPendingValues(null);
    setOptions([]);
    setOptionsOpen(false);
    setPlacesError("");
    setResolvingPosition(false);
  };

  const previewEntries = useMemo(() => {
    if (!pendingValues) return [];
    const definitions = [
      [fields.street, "Οδός"],
      [fields.streetNumber, "Αριθμός"],
      [fields.zip, "ΤΚ"],
      [fields.city, "Πόλη"],
    ];

    return buildAddressPreviewEntries({
      definitions,
      pendingValues,
      getCurrentValue: getValues,
    });
  }, [fields, getValues, pendingValues]);

  const confirmSelection = () => {
    Object.entries(pendingValues || {}).forEach(([fieldName, value]) => {
      setValue(fieldName, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
    onResolved?.({ ...getValues(), ...(pendingValues || {}) });
    handleClose();
  };

  return (
    <>
      <Button
        size="small"
        startIcon={<MapOutlinedIcon />}
        onClick={handleOpen}
        sx={{ textTransform: "none" }}
      >
        Εντοπισμός στο Google Maps
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography variant="h6">Εντοπισμός διαμερίσματος</Typography>
          {!disabled && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Ζωντανές προτάσεις
              </Typography>
              <Switch
                size="small"
                checked={liveSuggestions}
                onChange={(event) => {
                  setLiveSuggestions(event.target.checked);
                  setOptions([]);
                  setOptionsOpen(false);
                  setPlacesError("");
                }}
                inputProps={{ "aria-label": "Ζωντανές προτάσεις διεύθυνσης" }}
              />
            </Stack>
          )}
        </DialogTitle>
        <DialogContent>
          {!resolvedApiKey || loadError ? (
            <Typography color="error" role="alert">
              Ο χάρτης Google δεν είναι διαθέσιμος. Ελέγξτε το API key και τις
              επιτρεπόμενες υπηρεσίες.
            </Typography>
          ) : !isLoaded ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress aria-label="Φόρτωση χάρτη" />
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Box
                sx={{
                  width: "100%",
                  height: 300,
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <GoogleMap
                  mapContainerStyle={MAP_CONTAINER_STYLE}
                  center={previewPosition || GREECE_CENTER}
                  zoom={previewPosition ? PIN_ZOOM : GREECE_ZOOM}
                  onClick={handleMapClick}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: true,
                    fullscreenControl: true,
                  }}
                >
                  {previewPosition && (
                    <MarkerF
                      position={previewPosition}
                      draggable={!disabled}
                      onDragEnd={handleMarkerDragEnd}
                    />
                  )}
                </GoogleMap>
              </Box>

              {!disabled && (
                <MuiAutocomplete
                  key={liveSuggestions ? "live-mode" : "manual-mode"}
                  freeSolo
                  loading={loadingSuggestions}
                  options={options}
                  open={optionsOpen}
                  filterOptions={(items) => items}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option?.label || ""
                  }
                  inputValue={searchValue}
                  onOpen={() => {
                    if (options.length) setOptionsOpen(true);
                  }}
                  onClose={() => setOptionsOpen(false)}
                  onInputChange={(_event, value) => {
                    setSearchValue(value || "");
                    setPlacesError("");

                    if (shouldHideSuggestionsOnInput(liveSuggestions)) {
                      requestIdRef.current += 1;
                      setOptions([]);
                      setOptionsOpen(false);
                      setLoadingSuggestions(false);
                    }
                  }}
                  onChange={(_event, option) => handleSuggestionSelect(option)}
                  renderInput={(params) => (
                    <CssTextField
                      {...params}
                      variant={fieldsVariant ?? "standard"}
                      placeholder="Αναζήτηση διεύθυνσης"
                      error={Boolean(placesError)}
                      helperText={placesError}
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        endAdornment: (
                          <>
                            {!liveSuggestions && (
                              <IconButton
                                size="small"
                                aria-label="Αναζήτηση διεύθυνσης"
                                onMouseDown={(event) =>
                                  event.preventDefault()
                                }
                                onClick={async (event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  await handleTypedSearch();
                                }}
                              >
                                <SearchIcon fontSize="small" />
                              </IconButton>
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      onKeyDown={async (event) => {
                        if (event.key !== "Enter") return;

                        const action = getEnterAction({
                          liveSuggestions,
                          optionsOpen,
                        });
                        if (action === "select-option") return;

                        event.preventDefault();
                        event.stopPropagation();
                        await handleTypedSearch();
                      }}
                    />
                  )}
                />
              )}

              {resolvingPosition && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  role="status"
                >
                  Εντοπισμός διεύθυνσης…
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Ακύρωση</Button>
          {!disabled && (
            <Button
              variant="contained"
              onClick={() => setConfirmOpen(true)}
              disabled={!pendingValues || resolvingPosition}
            >
              Εφαρμογή
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Εφαρμογή σημείου χάρτη</DialogTitle>
        <DialogContent>
          {previewEntries.length ? (
            <Stack spacing={1}>
              {previewEntries.map((entry) => (
                <Box
                  key={entry.fieldName}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                    borderBottom: (theme) =>
                      `1px solid ${theme.palette.divider}`,
                    py: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {entry.label}
                  </Typography>
                  <Typography variant="body2">{entry.nextValue}</Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography>
              Να χρησιμοποιηθεί το επιλεγμένο σημείο για το διαμέρισμα;
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Ακύρωση</Button>
          <Button variant="contained" onClick={confirmSelection}>
            Επιβεβαίωση
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GmapsPopulator;
